import { useQuery } from "@tanstack/react-query";
import SysRecord from "app/class/record";
import { RecordManagement } from "app/class/record-management";
import queryKeys from "shared/services/queryKeys";
import { useAuth } from "app/providers/AuthProvider";

const PLATFORM_ADMINISTRATOR_ROLE = "platform_administrator";

interface MenuNode {
    record: SysRecord;
    children: MenuNode[];
}

export function useMenuDefinition() {
    const { user } = useAuth();

    const buildMenuTree = (records: SysRecord[]): MenuNode[] => {
        const sorted = [...records].sort((a, b) => a.order - b.order);

        const map = new Map<string, MenuNode>();
        const roots: MenuNode[] = [];

        // Create all nodes first
        sorted.forEach((record) => {
            map.set(record.id.toString(), {
                record,
                children: [],
            });
        });

        // Build hierarchy preserving order
        sorted.forEach((record) => {
            const parentId = record.parent;
            const node = map.get(record.id.toString())!;

            if (parentId && map.has(parentId)) {
                map.get(parentId)!.children.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    };

    const parseMenuRoles = (rawRoles: unknown): string[] => {
        if (Array.isArray(rawRoles)) {
            return rawRoles.map(String).map((role) => role.trim()).filter(Boolean);
        }

        if (typeof rawRoles !== "string") {
            return [];
        }

        const trimmedRoles = rawRoles.trim();
        if (!trimmedRoles) {
            return [];
        }

        try {
            const parsedRoles = JSON.parse(trimmedRoles);
            if (Array.isArray(parsedRoles)) {
                return parsedRoles.map(String).map((role) => role.trim()).filter(Boolean);
            }
        } catch {
            // Fall back to comma-separated metadata values.
        }

        return trimmedRoles.split(",").map((role) => role.trim()).filter(Boolean);
    };

    const canReadMenuItem = (record: SysRecord) => {
        const currentUserRoles = new Set((user?.roles ?? []).map(String));
        if (user?.isAdmin || currentUserRoles.has(PLATFORM_ADMINISTRATOR_ROLE)) {
            return true;
        }

        const menuRoles = parseMenuRoles((record as any).roles);
        if (menuRoles.length === 0) {
            return true;
        }

        return menuRoles.some((role) => currentUserRoles.has(role));
    };

    const filterMenuTreeByRoles = (nodes: MenuNode[]): MenuNode[] =>
        nodes
            .map((node) => {
                if (!canReadMenuItem(node.record)) {
                    return null;
                }

                return {
                    ...node,
                    children: filterMenuTreeByRoles(node.children),
                };
            })
            .filter((node): node is MenuNode => Boolean(node));

    const { data: responseMenuRecord } = useQuery({
        queryKey: queryKeys.menu.tree(),
        queryFn: () => new RecordManagement("sys_menu_item").orderBy("order").paginate(1, 5000).run(),
    });

    const records = responseMenuRecord?.data ?? [];
    const menuTree = filterMenuTreeByRoles(buildMenuTree(records));

    return {
        menuTree
    }
}
